/* =============================================================
   pi-qa — Community Q&A tool (public widget runtime, AB#9284)

   Ported from the PublicInput Frontend Public Design System handoff
   ("Community QA tool.html"), rebuilt for live data: all filtering,
   sorting, searching, and paging are SERVER-side (QaController), and
   upvotes ride the existing /Comments/UpVote|UnVote endpoints
   (questions are PollComments).

   Vanilla JS, no dependencies. Initializes every [data-pi-qa] element
   as an independent instance. Serves BOTH public surfaces:
     - project pages (shell emitted by Poll.RenderCommunityQa)
     - hub pages (shell emitted by the CommunityQA Puck widget)

   XSS contract: every server/user string is inserted via textContent —
   never innerHTML interpolation. innerHTML appears ONLY for the constant
   SVG icon strings below (no interpolation, trusted static markup).

   Shell attributes (all optional except data-qa-poll):
     data-qa-poll     poll id of the Community Q&A instance (required)
     data-qa-heading  widget heading (hub widget; project pages use the poll header)
     data-qa-intro    intro/description paragraph
     data-qa-search   "0" hides the search box
     data-qa-status   "0" hides the status filter
     data-qa-cat      "0" hides the category filter
     data-qa-sort     default sort: "top" (default) | "new"
     data-qa-submit   "0" hides Ask a question (read-only Q&A)
   ============================================================= */

// The widget's stylesheet must ship with THIS bundle: project poll pages load only
// qa-tool.bundle.js (hub pages get the same import via puckBundle). Without it the
// markup's default state is broken (hidden panels and notices all visible).
import "../Content/qa-tool.css";

var PAGE_SIZE = 10;
var SEARCH_DEBOUNCE_MS = 300;
var DEFAULT_QUESTION_LENGTH = 500;
var AVATAR_PALETTE_SIZE = 6;

/* Submit-result messages that have no server-supplied text (429 carries no body; the
   generic fallback covers 5xx/network/unknown result strings). */
var MSG_RATE_LIMITED =
  "You're sending questions too quickly. Please wait a minute and try again.";
var MSG_SUBMIT_GENERIC =
  "Something went wrong on our end and your question was not saved. Please try again — if the problem continues, contact the project team using the details under “Who’s listening?”.";

/* Constant, trusted SVG icon markup (the ONLY innerHTML in this module). */
var ICON = {
  up: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 19V5M5 12l7-7 7 7"></path></svg>',
  chev: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6"></path></svg>',
  check:
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 6L9 17l-5-5"></path></svg>',
  eye: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"></path><circle cx="12" cy="12" r="3"></circle></svg>',
  shield:
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"></path></svg>',
  search:
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="7"></circle><path d="M21 21l-4.3-4.3"></path></svg>',
  bubble:
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"></path></svg>',
  people:
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="9" cy="8" r="3.5"></circle><path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5"></path><path d="M17 5.5a4.5 4.5 0 010 7M19.5 3a8 8 0 010 12"></path></svg>',
  plus: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14"></path></svg>',
  ok: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M22 11.1V12a10 10 0 11-5.9-9.1"></path><path d="M22 4L12 14l-3-3"></path></svg>',
  err: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 9v4M12 17h.01"></path><circle cx="12" cy="12" r="9"></circle></svg>'
};

/* ---------- small helpers ---------- */

function make(tag, className, text) {
  var node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined && text !== null && text !== "")
    node.textContent = text;
  return node;
}

function icon(name) {
  var span = make("span");
  span.innerHTML = ICON[name]; // trusted constant markup only (SVGs carry their own aria-hidden)
  return span.firstChild;
}

function nameHash(name) {
  var h = 0;
  var s = name || "";
  for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % AVATAR_PALETTE_SIZE;
}

function initialsOf(name) {
  var parts = (name || "").trim().split(/\s+/);
  var first = parts[0] ? parts[0].charAt(0) : "";
  var second = parts[1] ? parts[1].charAt(0) : "";
  return (first + second).toUpperCase();
}

function avatarNode(name, url, extraClass) {
  var wrap = make(
    "span",
    "pi-qa-avatar pi-qa-avatar--" +
      nameHash(name) +
      (extraClass ? " " + extraClass : "")
  );
  if (url && /^https?:\/\//i.test(url)) {
    var img = make("img", "pi-qa-avatar__img");
    img.src = url;
    img.alt = name || "Responder";
    wrap.appendChild(img);
  } else {
    wrap.setAttribute("aria-hidden", "true");
    wrap.appendChild(document.createTextNode(initialsOf(name)));
  }
  return wrap;
}

function relativeTime(isoOrDate) {
  var then = new Date(isoOrDate);
  if (isNaN(then.getTime())) return "";
  var seconds = Math.max(0, (Date.now() - then.getTime()) / 1000);
  var MIN = 60,
    HOUR = 3600,
    DAY = 86400,
    WEEK = 604800;
  if (seconds < MIN) return "just now";
  if (seconds < HOUR) {
    var m = Math.floor(seconds / MIN);
    return m + (m === 1 ? " minute ago" : " minutes ago");
  }
  if (seconds < DAY) {
    var h = Math.floor(seconds / HOUR);
    return h + (h === 1 ? " hour ago" : " hours ago");
  }
  if (seconds < WEEK * 5) {
    var d = Math.floor(seconds / DAY);
    if (d < 7) return d + (d === 1 ? " day ago" : " days ago");
    var w = Math.floor(d / 7);
    return w + (w === 1 ? " week ago" : " weeks ago");
  }
  return then.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

function longDate(isoOrDate) {
  var d = new Date(isoOrDate);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
}

/* ---------- Core API networking (AB#9284: /Qa/* MVC → CityzenApiCore) ----------
   The widget's data now comes from CityzenApiCore (a different origin), reached via
   window._JSCONFIG.ReactAppApiCoreBaseUrl (the same config the public meeting
   dashboard uses). Auth is a short-lived viewer JWT: POST api/v1/qa/exchange once
   (anonymous, poll-scoped), cache it with an expiry margin, and send it as a Bearer
   token on every Qa API call. The old /Qa/Token anti-forgery flow is gone.

   Upvotes intentionally still ride the same-origin /Comments/UpVote|UnVote endpoints
   ([AllowCrossSiteJson] — no anti-forgery token required), so they keep cookie auth. */

function apiBaseUrl() {
  return (
    (typeof window !== "undefined" &&
      window._JSCONFIG &&
      window._JSCONFIG.ReactAppApiCoreBaseUrl) ||
    ""
  );
}

/* Proof of who this browser is, minted server-side by CityzenWeb (Cityzen.QaVisitorToken) over
   an identity we can prove the visitor owns — the authenticated user, the anonymous-session
   cookie, or the participant cookie. The Core API is a separate origin and can't see any of
   those cookies, so the token travels in the exchange body; the API verifies its HMAC against
   the shared jwt-secret before binding the id into the viewer JWT.

   It is deliberately NOT window.cz.ParticipantUserId. That value honours a ?userId= query param
   for unauthenticated callers, so sending it bare would let anyone exchange for another
   resident's identity and read that resident's hidden, pending questions. Empty is fine and
   safe: the visitor just gets no personalisation (no votedByMe, no author-only pending rows). */
function visitorToken() {
  return (
    (typeof window !== "undefined" &&
      window.cz &&
      window.cz.QaVisitorToken) ||
    ""
  );
}

// One cached viewer JWT per poll id (widgets on the same page share by poll).
var qaTokenCache = {};

function exchangeQaToken(pollId, force) {
  var cached = qaTokenCache[pollId];
  if (!force && cached && cached.token && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.token);
  }
  var base = apiBaseUrl();
  if (!base) {
    if (window.console && console.error)
      console.error(
        "pi-qa: window._JSCONFIG.ReactAppApiCoreBaseUrl is not configured — the Community Q&A widget cannot reach the API."
      );
    return Promise.reject(new Error("Q&A API base URL is not configured."));
  }
  return fetch(base + "/api/v1/qa/exchange", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // Visitor identity travels ONLY in the exchange request; the server verifies the signed
    // token and re-signs the id into the JWT claim used by the read/submit paths.
    // Personalization (votedByMe, the author-only "pending" rows) therefore reflects the
    // identity present at exchange time — since the token is cached per poll for its ~30-min
    // lifetime, an identity that appears later isn't picked up until the token is
    // re-exchanged. Acceptable: it is stable for a page's lifetime.
    body: JSON.stringify({ pollId: pollId, visitorToken: visitorToken() })
  })
    .then(function (res) {
      if (!res.ok) throw new Error("HTTP " + res.status);
      return res.json();
    })
    .then(function (payload) {
      var data = (payload && payload.data) || {};
      if (!data.accessToken)
        throw new Error("Q&A token exchange returned no token.");
      // Re-exchange a few minutes before the ~30-min JWT actually expires.
      var ttlSeconds = data.expiresIn > 0 ? data.expiresIn : 1800;
      var marginMs = Math.min(300, Math.floor(ttlSeconds / 2)) * 1000;
      qaTokenCache[pollId] = {
        token: data.accessToken,
        customerId: data.customerId || 0,
        expiresAt: Date.now() + ttlSeconds * 1000 - marginMs
      };
      return data.accessToken;
    });
}

function qaAuthHeaders(pollId) {
  var cached = qaTokenCache[pollId];
  var headers = { Authorization: "Bearer " + (cached ? cached.token : "") };
  if (cached && cached.customerId)
    headers["X-Tenant-ID"] = String(cached.customerId);
  return headers;
}

// Exchange (if needed) → fetch → on a 401 re-exchange once and retry. Resolves to a Response.
function qaApiFetch(pollId, method, path, body, isRetry) {
  return exchangeQaToken(pollId, false).then(function () {
    var init = { method: method, headers: qaAuthHeaders(pollId) };
    if (body !== undefined && body !== null) {
      init.headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    return fetch(apiBaseUrl() + path, init).then(function (res) {
      if (res.status === 401 && !isRetry) {
        return exchangeQaToken(pollId, true).then(function () {
          return qaApiFetch(pollId, method, path, body, true);
        });
      }
      return res;
    });
  });
}

// GET → returns the unwrapped ApiResponse `data` payload (Core API camelCase envelope).
function qaApiGet(pollId, path) {
  return qaApiFetch(pollId, "GET", path, null, false).then(function (res) {
    if (!res.ok) throw new Error("HTTP " + res.status);
    return res.json().then(unwrap);
  });
}

// POST → tolerates a 204/empty body (view ping and non-generic-Result commands).
function qaApiPost(pollId, path, body) {
  return qaApiFetch(pollId, "POST", path, body || {}, false).then(function (
    res
  ) {
    if (!res.ok) throw new Error("HTTP " + res.status);
    if (res.status === 204) return null;
    return res.text().then(function (text) {
      return text ? unwrap(JSON.parse(text)) : null;
    });
  });
}

function unwrap(payload) {
  return payload && typeof payload === "object" && "data" in payload
    ? payload.data
    : payload;
}

// Same-origin form POST kept ONLY for the retained /Comments/UpVote|UnVote endpoints.
function postVoteForm(url, data) {
  var body = new URLSearchParams();
  Object.keys(data).forEach(function (key) {
    if (data[key] !== undefined && data[key] !== null)
      body.append(key, data[key]);
  });
  return fetch(url, {
    method: "POST",
    credentials: "same-origin",
    body: body
  });
}

/* ---------- one widget instance ---------- */

function QaWidget(root) {
  var cfg = {
    pollId: parseInt(root.getAttribute("data-qa-poll"), 10),
    heading: root.getAttribute("data-qa-heading") || "",
    intro: root.getAttribute("data-qa-intro") || "",
    showSearch: root.getAttribute("data-qa-search") !== "0",
    showStatus: root.getAttribute("data-qa-status") !== "0",
    showCat: root.getAttribute("data-qa-cat") !== "0",
    sort: root.getAttribute("data-qa-sort") === "new" ? "new" : "top",
    allowSubmission: root.getAttribute("data-qa-submit") !== "0"
  };
  if (!cfg.pollId || isNaN(cfg.pollId)) return;

  var state = {
    tab: "questions",
    status: "all",
    categoryId: 0,
    search: "",
    sort: cfg.sort,
    page: 0,
    questions: [],
    hasMore: false,
    counts: { open: 0, answered: 0, all: 0 },
    categories: [],
    responders: null, // lazily loaded
    serverAllowsSubmission: true,
    characterLimit: DEFAULT_QUESTION_LENGTH,
    openAnswers: {},
    viewedIds: {},
    voteInFlight: {},
    fetchSeq: 0,
    loading: false,
    loadFailed: false
  };

  var uid = "pi-qa-" + cfg.pollId + "-" + Math.floor(Math.random() * 100000);
  var refs = {};
  var searchTimer = null;

  /* ---------- shell ---------- */

  function buildShell() {
    root.classList.add("pi-qa");
    root.textContent = "";

    var region = make("section", "pi-qa__card");
    region.setAttribute("aria-label", cfg.heading || "Community questions");

    var head = make("div", "pi-qa__head");
    if (cfg.heading)
      head.appendChild(make("h2", "pi-qa__heading", cfg.heading));
    if (cfg.intro) head.appendChild(make("p", "pi-qa__intro", cfg.intro));
    if (cfg.heading || cfg.intro) region.appendChild(head);

    // Tabs
    var tabs = make("div", "pi-qa__tabs");
    tabs.setAttribute("role", "tablist");
    tabs.setAttribute("aria-label", "Community questions sections");

    refs.tabQuestions = tabButton("questions", "Questions", "bubble", true);
    refs.tabCount = make("span", "pi-qa__tab-count", "0");
    refs.tabQuestions.appendChild(refs.tabCount);
    refs.tabListening = tabButton(
      "listening",
      "Who's listening?",
      "people",
      false
    );
    tabs.appendChild(refs.tabQuestions);
    tabs.appendChild(refs.tabListening);
    region.appendChild(tabs);

    var body = make("div", "pi-qa__body");

    refs.panelQuestions = make("div", "pi-qa__panel pi-qa__panel--show");
    refs.panelQuestions.id = uid + "-panel-questions";
    refs.panelQuestions.setAttribute("role", "tabpanel");
    refs.panelQuestions.setAttribute("aria-labelledby", uid + "-tab-questions");
    refs.panelQuestions.setAttribute("tabindex", "0");
    buildQuestionsPanel(refs.panelQuestions);

    refs.panelListening = make("div", "pi-qa__panel");
    refs.panelListening.id = uid + "-panel-listening";
    refs.panelListening.setAttribute("role", "tabpanel");
    refs.panelListening.setAttribute("aria-labelledby", uid + "-tab-listening");
    refs.panelListening.setAttribute("tabindex", "0");

    body.appendChild(refs.panelQuestions);
    body.appendChild(refs.panelListening);
    region.appendChild(body);
    root.appendChild(region);
  }

  function tabButton(key, label, iconName, selected) {
    var btn = make("button", "pi-qa__tab");
    btn.type = "button";
    btn.id = uid + "-tab-" + key;
    btn.setAttribute("role", "tab");
    btn.setAttribute("aria-selected", selected ? "true" : "false");
    btn.setAttribute("aria-controls", uid + "-panel-" + key);
    btn.setAttribute("tabindex", selected ? "0" : "-1");
    btn.setAttribute("data-qa-tab", key);
    btn.appendChild(icon(iconName));
    btn.appendChild(document.createTextNode(" " + label));
    return btn;
  }

  function buildQuestionsPanel(panel) {
    // Controls row
    var controls = make("div", "pi-qa__controls");

    if (cfg.showSearch) {
      var searchWrap = make("div", "pi-qa__search");
      var searchLabel = make("label", "pi-qa-vh", "Search questions");
      searchLabel.setAttribute("for", uid + "-search");
      searchWrap.appendChild(searchLabel);
      searchWrap.appendChild(icon("search"));
      refs.search = make("input", "pi-qa__search-input");
      refs.search.id = uid + "-search";
      refs.search.type = "text";
      refs.search.placeholder = "Search questions…";
      searchWrap.appendChild(refs.search);
      controls.appendChild(searchWrap);
    }

    if (cfg.showStatus) {
      refs.status = labeledSelect(controls, "status", "Status", [
        { value: "all", label: "All" },
        { value: "open", label: "Open" },
        { value: "answered", label: "Answered" }
      ]);
    }

    if (cfg.showCat) {
      refs.category = labeledSelect(controls, "cat", "Category", [
        { value: "0", label: "All categories" }
      ]);
    }

    refs.sort = labeledSelect(controls, "sort", "Sort by", [
      { value: "top", label: "Most upvoted" },
      { value: "new", label: "Newest" }
    ]);
    refs.sort.value = state.sort;

    refs.askBtn = make("button", "pi-qa__btn pi-qa__btn--primary pi-qa__ask");
    refs.askBtn.type = "button";
    refs.askBtn.setAttribute("aria-expanded", "false");
    refs.askBtn.setAttribute("aria-controls", uid + "-compose");
    refs.askBtn.appendChild(icon("plus"));
    refs.askBtn.appendChild(document.createTextNode(" Ask a question"));
    if (!cfg.allowSubmission) refs.askBtn.classList.add("pi-qa-hidden");
    controls.appendChild(refs.askBtn);
    panel.appendChild(controls);

    // Compose form
    refs.compose = buildCompose();
    panel.appendChild(refs.compose);

    // Notices
    refs.okNotice = buildNotice("ok", "Question received", "");
    refs.errNotice = buildNotice(
      "err",
      "Your question couldn't be submitted",
      "Something went wrong on our end and your question was not saved. Please try again."
    );
    panel.appendChild(refs.okNotice);
    panel.appendChild(refs.errNotice);

    // Result count + live list
    refs.resCount = make("p", "pi-qa__rescount");
    refs.resCount.setAttribute("aria-hidden", "true");
    panel.appendChild(refs.resCount);

    // Small purpose-built live region: only the one-line summary is announced.
    // The list and empty state stay outside it, so a sort, filter, "Show more"
    // or answer toggle never re-reads the whole question list (FE-002).
    var live = make("div");
    live.setAttribute("aria-live", "polite");
    refs.liveSummary = make("p", "pi-qa-vh");
    live.appendChild(refs.liveSummary);
    panel.appendChild(live);
    refs.list = make("div", "pi-qa__list");
    panel.appendChild(refs.list);
    refs.empty = buildEmpty();
    panel.appendChild(refs.empty);

    var moreWrap = make("div", "pi-qa__more-wrap");
    refs.showMore = make(
      "button",
      "pi-qa__btn pi-qa__btn--outline",
      "Show more questions"
    );
    refs.showMore.type = "button";
    moreWrap.appendChild(refs.showMore);
    // A failed "Show more" reports here instead of destroying the loaded list (FE-001).
    refs.moreError = make("p", "pi-qa__more-error");
    refs.moreError.setAttribute("role", "status");
    refs.moreError.classList.add("pi-qa-hidden");
    moreWrap.appendChild(refs.moreError);
    refs.moreWrap = moreWrap;
    moreWrap.classList.add("pi-qa-hidden");
    panel.appendChild(moreWrap);
  }

  function labeledSelect(parent, key, labelText, options) {
    var wrap = make("div", "pi-qa__ctl");
    var label = make("label", "", labelText);
    label.setAttribute("for", uid + "-" + key);
    wrap.appendChild(label);
    var select = make("select", "pi-qa__select");
    select.id = uid + "-" + key;
    options.forEach(function (opt) {
      var o = make("option", "", opt.label);
      o.value = opt.value;
      select.appendChild(o);
    });
    wrap.appendChild(select);
    parent.appendChild(wrap);
    return select;
  }

  function buildCompose() {
    var form = make("form", "pi-qa__compose");
    form.id = uid + "-compose";
    form.noValidate = true;

    form.appendChild(
      make("p", "pi-qa__compose-h", "Ask the project team a question")
    );
    refs.composeSub = make(
      "p",
      "pi-qa__compose-sub",
      "Fields marked with * are required."
    );
    form.appendChild(refs.composeSub);

    var fields = make("div", "pi-qa__fields");

    // Question
    var fQuestion = make("div", "pi-qa__field pi-qa__field--full");
    var lQuestion = make("label", "", "Your question *");
    lQuestion.setAttribute("for", uid + "-in-question");
    refs.inQuestion = make("textarea", "");
    refs.inQuestion.id = uid + "-in-question";
    refs.inQuestion.maxLength = state.characterLimit;
    refs.inQuestion.setAttribute("aria-required", "true");
    refs.inQuestion.placeholder =
      "What would you like to know about this project?";
    refs.charCount = make(
      "span",
      "pi-qa__charct",
      "0 / " + state.characterLimit
    );
    refs.charCount.setAttribute("aria-hidden", "true");
    var eQuestion = errMsg(
      fQuestion,
      uid + "-err-question",
      "Enter your question before submitting."
    );
    refs.inQuestion.setAttribute("aria-describedby", eQuestion.id);
    fQuestion.appendChild(lQuestion);
    fQuestion.appendChild(refs.inQuestion);
    fQuestion.appendChild(refs.charCount);
    fQuestion.appendChild(eQuestion);
    refs.fQuestion = fQuestion;
    fields.appendChild(fQuestion);

    // Category (optional)
    refs.fCat = make("div", "pi-qa__field pi-qa__field--full pi-qa-hidden");
    var lCat = make("label", "", "Category (optional)");
    lCat.setAttribute("for", uid + "-in-cat");
    refs.inCat = make("select", "pi-qa__select");
    refs.inCat.id = uid + "-in-cat";
    refs.fCat.appendChild(lCat);
    refs.fCat.appendChild(refs.inCat);
    fields.appendChild(refs.fCat);

    // Name
    var fName = make("div", "pi-qa__field");
    var lName = make("label", "", "Name *");
    lName.setAttribute("for", uid + "-in-name");
    refs.inName = make("input", "");
    refs.inName.id = uid + "-in-name";
    refs.inName.type = "text";
    refs.inName.autocomplete = "name";
    refs.inName.setAttribute("aria-required", "true");
    refs.inName.placeholder = "Enter name";
    var eName = errMsg(fName, uid + "-err-name", "Enter your name.");
    refs.inName.setAttribute("aria-describedby", eName.id);
    fName.appendChild(lName);
    fName.appendChild(refs.inName);
    fName.appendChild(eName);
    refs.fName = fName;
    fields.appendChild(fName);

    // Email
    var fEmail = make("div", "pi-qa__field");
    var lEmail = make("label", "", "Email *");
    lEmail.setAttribute("for", uid + "-in-email");
    refs.inEmail = make("input", "");
    refs.inEmail.id = uid + "-in-email";
    refs.inEmail.type = "email";
    refs.inEmail.autocomplete = "email";
    refs.inEmail.setAttribute("aria-required", "true");
    refs.inEmail.placeholder = "Enter email";
    var eEmail = errMsg(
      fEmail,
      uid + "-err-email",
      "Enter a valid email address."
    );
    refs.inEmail.setAttribute("aria-describedby", eEmail.id);
    fEmail.appendChild(lEmail);
    fEmail.appendChild(refs.inEmail);
    fEmail.appendChild(eEmail);
    refs.fEmail = fEmail;
    fields.appendChild(fEmail);

    form.appendChild(fields);

    var foot = make("div", "pi-qa__compose-foot");
    refs.submitBtn = make(
      "button",
      "pi-qa__btn pi-qa__btn--primary",
      "Submit question"
    );
    refs.submitBtn.type = "submit";
    var cancel = make("button", "pi-qa__btn pi-qa__btn--ghost", "Cancel");
    cancel.type = "button";
    cancel.addEventListener("click", function () {
      toggleCompose(false);
    });
    foot.appendChild(refs.submitBtn);
    foot.appendChild(cancel);
    foot.appendChild(
      make(
        "span",
        "pi-qa__compose-note",
        "Your name may be shown with your question. Your email is never shown publicly."
      )
    );
    form.appendChild(foot);

    return form;
  }

  function errMsg(field, id, text) {
    var span = make("span", "pi-qa__err-msg", text);
    span.id = id;
    return span;
  }

  function buildNotice(kind, title, desc) {
    var box = make("div", "pi-qa__notice pi-qa__notice--" + kind);
    box.setAttribute("role", kind === "ok" ? "status" : "alert");
    box.setAttribute("tabindex", "-1");
    box.appendChild(icon(kind));
    var textWrap = make("div");
    textWrap.appendChild(make("p", "pi-qa__notice-t", title));
    var d = make("p", "pi-qa__notice-d", desc);
    textWrap.appendChild(d);
    if (kind === "err") {
      var retry = make("button", "pi-qa__btn pi-qa__btn--outline", "Try again");
      retry.type = "button";
      retry.addEventListener("click", function () {
        hideNotices();
        toggleCompose(true);
      });
      textWrap.appendChild(retry);
    }
    box.appendChild(textWrap);
    if (kind === "ok") refs.okNoticeDesc = d;
    return box;
  }

  function buildEmpty() {
    var box = make("div", "pi-qa__empty");
    box.appendChild(icon("bubble"));
    refs.emptyTitle = make("p", "pi-qa__empty-t", "No questions yet");
    refs.emptyDesc = make(
      "p",
      "pi-qa__empty-d",
      "Be the first to ask the project team a question."
    );
    box.appendChild(refs.emptyTitle);
    box.appendChild(refs.emptyDesc);
    refs.emptyAsk = make(
      "button",
      "pi-qa__btn pi-qa__btn--primary",
      "Ask a question"
    );
    refs.emptyAsk.type = "button";
    refs.emptyAsk.addEventListener("click", function () {
      toggleCompose(true);
    });
    box.appendChild(refs.emptyAsk);
    return box;
  }

  /* ---------- rendering ---------- */

  function renderCategoryOptions() {
    [refs.category, refs.inCat].forEach(function (select) {
      if (!select) return;
      var keep = select === refs.category ? 1 : 0;
      while (select.options.length > keep) select.remove(keep);
      if (select === refs.inCat) {
        var choose = make("option", "", "Choose a category…");
        choose.value = "0";
        select.appendChild(choose);
      }
      state.categories.forEach(function (cat) {
        var o = make("option", "", cat.name);
        o.value = String(cat.id);
        select.appendChild(o);
      });
    });
    var hasCats = state.categories.length > 0;
    if (refs.fCat) refs.fCat.classList.toggle("pi-qa-hidden", !hasCats);
    if (refs.category)
      refs.category.parentNode.classList.toggle(
        "pi-qa-hidden",
        !cfg.showCat || !hasCats
      );
  }

  function renderRow(q) {
    var row = make(
      "article",
      "pi-qa__row" + (q.pending ? " pi-qa__row--pending" : "")
    );
    row.setAttribute("data-qa-row", q.commentId);
    var main = make("div", "pi-qa__row-main");

    if (q.pending) {
      main.appendChild(make("div"));
    } else {
      var vote = make("button", "pi-qa__vote");
      vote.type = "button";
      vote.setAttribute("data-qa-act", "vote");
      vote.setAttribute("data-qa-id", q.commentId);
      vote.setAttribute("aria-pressed", q.votedByMe ? "true" : "false");
      vote.setAttribute("aria-label", voteButtonLabel(q));
      vote.appendChild(icon("up"));
      var voteCount = make("span", "pi-qa__vote-n", String(q.voteCount));
      voteCount.setAttribute("aria-hidden", "true");
      var voteLabel = make("span", "pi-qa__vote-l", "votes");
      voteLabel.setAttribute("aria-hidden", "true");
      vote.appendChild(voteCount);
      vote.appendChild(voteLabel);
      main.appendChild(vote);
    }

    var body = make("div", "pi-qa__row-body");

    var top = make("div", "pi-qa__row-top");
    top.appendChild(make("p", "pi-qa__qtext", q.text));
    var pill = make(
      "span",
      "pi-qa__pill " +
        (q.answered ? "pi-qa__pill--answered" : "pi-qa__pill--open")
    );
    if (q.answered) pill.appendChild(icon("check"));
    pill.appendChild(document.createTextNode(q.answered ? "Answered" : "Open"));
    top.appendChild(pill);
    body.appendChild(top);

    var meta = make("div", "pi-qa__meta");
    var asked = q.pending
      ? "Asked by you · " + relativeTime(q.createdUtc)
      : "Asked by " +
        (q.askerName || "Anonymous") +
        " · " +
        relativeTime(q.createdUtc);
    meta.appendChild(make("span", "", asked));
    if (q.categoryName) {
      meta.appendChild(make("span", "pi-qa__dot", "·"));
      meta.appendChild(make("span", "pi-qa__cat-chip", q.categoryName));
    }
    if (!q.pending) {
      meta.appendChild(make("span", "pi-qa__dot", "·"));
      var views = make("span", "pi-qa__views");
      views.appendChild(icon("eye"));
      views.appendChild(document.createTextNode(" " + q.viewCount + " views"));
      meta.appendChild(views);
    }
    body.appendChild(meta);

    if (q.pending) {
      var note = make("span", "pi-qa__pending-note");
      note.appendChild(icon("eye"));
      var noteText = make("span");
      var strong = make("strong", "", "Visible only to you.");
      noteText.appendChild(strong);
      noteText.appendChild(
        document.createTextNode(
          " Your question is awaiting review by the project team and will appear publicly once approved."
        )
      );
      note.appendChild(noteText);
      body.appendChild(note);
    } else if (q.answered && q.answer) {
      var isOpen = !!state.openAnswers[q.commentId];
      var disc = make("button", "pi-qa__ansdisc");
      disc.type = "button";
      disc.setAttribute("data-qa-act", "answer");
      disc.setAttribute("data-qa-id", q.commentId);
      disc.setAttribute("aria-expanded", isOpen ? "true" : "false");
      disc.setAttribute("aria-controls", uid + "-ans-" + q.commentId);
      disc.appendChild(icon("chev"));
      disc.appendChild(
        document.createTextNode(
          isOpen ? " Hide official answer" : " View official answer"
        )
      );
      body.appendChild(disc);

      var answer = make(
        "div",
        "pi-qa__answer" + (isOpen ? " pi-qa__answer--show" : "")
      );
      answer.id = uid + "-ans-" + q.commentId;
      var ansHead = make("div", "pi-qa__ans-head");
      ansHead.appendChild(
        avatarNode(q.answer.responderName, q.answer.responderAvatarUrl)
      );
      var who = make("div", "pi-qa__ans-who");
      var nameLine = make(
        "span",
        "pi-qa__ans-name",
        q.answer.responderName || "Project team"
      );
      var badge = make("span", "pi-qa__ans-badge");
      badge.appendChild(icon("shield"));
      badge.appendChild(document.createTextNode(" Official answer"));
      nameLine.appendChild(badge);
      who.appendChild(nameLine);
      if (q.answer.responderTitle)
        who.appendChild(
          make("span", "pi-qa__ans-role", q.answer.responderTitle)
        );
      ansHead.appendChild(who);
      answer.appendChild(ansHead);
      answer.appendChild(make("p", "pi-qa__ans-body", q.answer.text));
      if (q.answer.publishedUtc)
        answer.appendChild(
          make(
            "p",
            "pi-qa__ans-date",
            "Answered " + longDate(q.answer.publishedUtc)
          )
        );
      body.appendChild(answer);
    } else {
      body.appendChild(
        make(
          "p",
          "pi-qa__awaiting",
          "Awaiting an answer from the project team."
        )
      );
    }

    main.appendChild(body);
    row.appendChild(main);
    return row;
  }

  function renderList(append) {
    if (!append) refs.list.textContent = "";
    var startIndex = append ? refs.list.children.length : 0;

    state.questions.slice(startIndex).forEach(function (q) {
      refs.list.appendChild(renderRow(q));
    });

    var count = state.questions.length;
    var isEmpty = count === 0;
    var isFiltered = !!(
      state.search ||
      state.status !== "all" ||
      state.categoryId > 0
    );

    refs.list.classList.toggle("pi-qa-hidden", isEmpty);
    refs.empty.classList.toggle("pi-qa__empty--show", isEmpty);
    if (isEmpty) {
      refs.emptyTitle.textContent = isFiltered
        ? "No matching questions"
        : "No questions yet";
      refs.emptyDesc.textContent = isFiltered
        ? "Try a different search term or clear the filters."
        : "Be the first to ask the project team a question.";
      refs.emptyAsk.classList.toggle(
        "pi-qa-hidden",
        isFiltered || !cfg.allowSubmission || !state.serverAllowsSubmission
      );
    }

    refs.tabCount.textContent = String(state.counts.all);
    // "Showing 10 of 42 questions" when more pages remain and we know the total;
    // "Showing 10 questions" otherwise. Never "of more" (FE-002).
    var total = state.counts && state.counts.all;
    var summary = count
      ? "Showing " +
        count +
        (state.hasMore && total > count ? " of " + total : "") +
        " question" +
        (count === 1 ? "" : "s")
      : "";
    refs.resCount.textContent = summary;
    refs.liveSummary.textContent = summary ? summary + "." : "";
    refs.moreWrap.classList.toggle("pi-qa-hidden", !state.hasMore);
  }

  function renderListening() {
    refs.panelListening.textContent = "";
    refs.panelListening.appendChild(
      make(
        "p",
        "pi-qa__wl-intro",
        "Questions on this page go directly to the project team. Answers are posted publicly by the team members below."
      )
    );
    var grid = make("div", "pi-qa__wl-grid");
    (state.responders || []).forEach(function (r) {
      var card = make("div", "pi-qa__wl-card");
      card.appendChild(avatarNode(r.name, r.avatarUrl, "pi-qa-avatar--lg"));
      var info = make("div");
      info.appendChild(make("p", "pi-qa__wl-name", r.name));
      if (r.title) info.appendChild(make("p", "pi-qa__wl-role", r.title));
      info.appendChild(
        make(
          "span",
          "pi-qa__wl-stat",
          r.answersPosted +
            (r.answersPosted === 1 ? " answer posted" : " answers posted")
        )
      );
      card.appendChild(info);
      grid.appendChild(card);
    });
    if (!state.responders || !state.responders.length) {
      refs.panelListening.appendChild(
        make("p", "pi-qa__wl-empty", "The project team will be listed here.")
      );
    } else {
      refs.panelListening.appendChild(grid);
    }
  }

  /* ---------- data ---------- */

  function loadQuestions(append) {
    state.loading = true;
    if (refs.moreError) refs.moreError.classList.add("pi-qa-hidden");
    // Stale-response guard: a slow earlier response must never overwrite a newer one.
    var seq = ++state.fetchSeq;
    // No viewerUserId param: the server derives the caller's identity from the signed JWT
    // claim minted at exchange (a request param would be spoofable).
    var params = new URLSearchParams({
      status: state.status,
      categoryId: state.categoryId,
      q: state.search,
      sort: state.sort,
      page: state.page,
      take: PAGE_SIZE
    });
    return qaApiGet(
      cfg.pollId,
      "/api/v1/qa/" + cfg.pollId + "/questions?" + params.toString()
    )
      .then(function (data) {
        if (seq !== state.fetchSeq) return;
        state.loadFailed = false;
        var incoming = data.questions || [];
        if (append) {
          // De-dupe by commentId: a locally-prepended submission can reappear in a later page.
          var seen = {};
          state.questions.forEach(function (q) {
            seen[q.commentId] = true;
          });
          incoming = incoming.filter(function (q) {
            return !seen[q.commentId];
          });
          state.questions = state.questions.concat(incoming);
        } else {
          state.questions = incoming;
        }
        state.hasMore = !!data.hasMore;
        // Append pages omit counts/categories/limits — keep the page-0 values.
        if (data.counts) state.counts = data.counts;
        if (data.categories) state.categories = data.categories;
        if (
          data.characterLimit > 0 &&
          data.characterLimit !== state.characterLimit
        ) {
          state.characterLimit = data.characterLimit;
          refs.inQuestion.maxLength = state.characterLimit;
          refs.charCount.textContent =
            refs.inQuestion.value.length + " / " + state.characterLimit;
        }
        if (data.allowSubmission !== undefined) {
          state.serverAllowsSubmission = data.allowSubmission !== false;
          if (!state.serverAllowsSubmission)
            refs.askBtn.classList.add("pi-qa-hidden");
        }
        renderCategoryOptions();
        renderList(append);
      })
      .catch(function (err) {
        if (window.console && console.error)
          console.error("pi-qa: failed to load questions", err);
        if (seq !== state.fetchSeq) return;
        if (append) {
          // Keep what the reader already has. Roll the page back so the retry
          // fetches the page that just failed instead of skipping it (FE-001).
          state.page = Math.max(0, state.page - 1);
          refs.moreError.textContent =
            "Couldn't load more questions. Please try again.";
          refs.moreError.classList.remove("pi-qa-hidden");
          return;
        }
        state.loadFailed = true;
        state.questions = [];
        renderList(false);
        refs.emptyTitle.textContent = "Questions couldn't be loaded";
        refs.emptyDesc.textContent = "Please refresh the page to try again.";
        refs.emptyAsk.classList.add("pi-qa-hidden");
        refs.empty.classList.add("pi-qa__empty--show");
      })
      .then(function () {
        if (seq === state.fetchSeq) state.loading = false;
      });
  }

  function loadResponders() {
    if (state.responders !== null) return Promise.resolve();
    return qaApiGet(cfg.pollId, "/api/v1/qa/" + cfg.pollId + "/responders")
      .then(function (data) {
        state.responders = data.responders || [];
        renderListening();
      })
      .catch(function () {
        state.responders = [];
        renderListening();
      });
  }

  function vote(q, button) {
    if (state.voteInFlight[q.commentId]) return;
    state.voteInFlight[q.commentId] = true;

    var wasVoted = q.votedByMe;
    // Optimistic flip
    q.votedByMe = !wasVoted;
    q.voteCount += q.votedByMe ? 1 : -1;
    updateVoteButton(button, q);

    var url = wasVoted ? "/Comments/UnVote" : "/Comments/UpVote";
    postVoteForm(url, {
      commentId: q.commentId,
      assetId: cfg.pollId,
      key: "poll"
    })
      .then(function (res) {
        return res.text();
      })
      .then(function (text) {
        var result = (text || "").replace(/^"|"$/g, "").trim();
        if (result === "duplicate") {
          // Server already had our vote: the pre-flip count included it, so restore
          // the count and settle on the voted state (no net change server-side).
          q.votedByMe = true;
          q.voteCount += wasVoted ? 1 : -1;
        } else if (result !== "success") {
          // Revert the optimistic flip.
          q.votedByMe = wasVoted;
          q.voteCount += wasVoted ? 1 : -1;
        }
        updateVoteButton(button, q);
      })
      .catch(function () {
        q.votedByMe = wasVoted;
        q.voteCount += wasVoted ? 1 : -1;
        updateVoteButton(button, q);
      })
      .then(function () {
        delete state.voteInFlight[q.commentId];
      });
  }

  // Named to avoid shadowing by renderRow's local voteLabel span element.
  function voteButtonLabel(q) {
    // Include a question excerpt so screen-reader users browsing by button can tell
    // which question each identically-styled vote control belongs to.
    var excerpt =
      (q.text || "").length > 60 ? q.text.slice(0, 57) + "…" : q.text;
    return 'Upvote: "' + excerpt + '" — ' + q.voteCount + " votes";
  }

  function updateVoteButton(button, q) {
    if (!button || !button.isConnected) return;
    button.setAttribute("aria-pressed", q.votedByMe ? "true" : "false");
    button.setAttribute("aria-label", voteButtonLabel(q));
    var n = button.querySelector(".pi-qa__vote-n");
    if (n) n.textContent = String(q.voteCount);
  }

  function pingView(q) {
    if (state.viewedIds[q.commentId]) return;
    state.viewedIds[q.commentId] = true;
    qaApiPost(
      cfg.pollId,
      "/api/v1/qa/" + cfg.pollId + "/questions/" + q.commentId + "/view",
      {}
    ).catch(function () {
      /* fire-and-forget */
    });
  }

  /* ---------- interactions ---------- */

  function switchTab(key) {
    state.tab = key;
    var isQuestions = key === "questions";
    refs.tabQuestions.setAttribute(
      "aria-selected",
      isQuestions ? "true" : "false"
    );
    refs.tabQuestions.setAttribute("tabindex", isQuestions ? "0" : "-1");
    refs.tabListening.setAttribute(
      "aria-selected",
      isQuestions ? "false" : "true"
    );
    refs.tabListening.setAttribute("tabindex", isQuestions ? "-1" : "0");
    refs.panelQuestions.classList.toggle("pi-qa__panel--show", isQuestions);
    refs.panelListening.classList.toggle("pi-qa__panel--show", !isQuestions);
    if (!isQuestions) loadResponders();
  }

  function toggleCompose(open) {
    if (!cfg.allowSubmission || !state.serverAllowsSubmission) return;
    var show =
      open === undefined
        ? !refs.compose.classList.contains("pi-qa__compose--show")
        : open;
    var hadFocusInside = refs.compose.contains(document.activeElement);
    refs.compose.classList.toggle("pi-qa__compose--show", show);
    refs.askBtn.setAttribute("aria-expanded", show ? "true" : "false");
    hideNotices();
    if (show) {
      refs.inQuestion.focus();
    } else if (hadFocusInside) {
      // Don't drop keyboard focus to <body> when the form closes under it.
      refs.askBtn.focus();
    }
  }

  function hideNotices() {
    refs.okNotice.classList.remove("pi-qa__notice--show");
    refs.errNotice.classList.remove("pi-qa__notice--show");
  }

  function setInvalid(field, input, invalid) {
    field.classList.toggle("pi-qa__field--invalid", invalid);
    if (invalid) {
      input.setAttribute("aria-invalid", "true");
    } else {
      input.removeAttribute("aria-invalid");
    }
  }

  function validate() {
    var firstInvalid = null;
    var questionBad = !refs.inQuestion.value.trim();
    setInvalid(refs.fQuestion, refs.inQuestion, questionBad);
    if (questionBad) firstInvalid = refs.inQuestion;

    var nameBad = !refs.inName.value.trim();
    setInvalid(refs.fName, refs.inName, nameBad);
    if (nameBad && !firstInvalid) firstInvalid = refs.inName;

    var emailBad = !/^\S+@\S+\.\S+$/.test(refs.inEmail.value.trim());
    setInvalid(refs.fEmail, refs.inEmail, emailBad);
    if (emailBad && !firstInvalid) firstInvalid = refs.inEmail;

    if (firstInvalid) {
      firstInvalid.focus();
      return false;
    }
    return true;
  }

  function submitQuestion() {
    if (!validate()) return;
    refs.submitBtn.disabled = true;

    // Use qaApiFetch (not qaApiPost) so we can read the HTTP status: the server now conveys
    // rate-limiting as 429 (empty body, from the qa-ask limiter) and FluentValidation
    // failures as 400 (envelope: { error: { description, errors:[{ description }] } }).
    // Everything the handler treats as a business outcome — success, "closed", "invalid"
    // (over character limit), "error" (fully-anonymous project) — comes back 200 with a
    // { result, message } DTO in the ApiResponse `data`.
    qaApiFetch(
      cfg.pollId,
      "POST",
      "/api/v1/qa/" + cfg.pollId + "/questions",
      {
        question: refs.inQuestion.value.trim(),
        askerName: refs.inName.value.trim(),
        askerEmail: refs.inEmail.value.trim(),
        categoryId: parseInt(refs.inCat.value || "0", 10)
      },
      false
    )
      .then(function (res) {
        if (res.status === 429) {
          showSubmitError(MSG_RATE_LIMITED);
          return;
        }
        if (res.status === 400) {
          // Surface the server's validation message (last line of defense behind client-side
          // validate()). Read the envelope; fall back to the generic notice if it's unshaped.
          return res.text().then(function (text) {
            showSubmitError(validationMessage(text) || MSG_SUBMIT_GENERIC);
          });
        }
        if (!res.ok) {
          // 404 (QA unpublished / poll gone), 5xx, anything else without a business DTO.
          showSubmitError(MSG_SUBMIT_GENERIC);
          return;
        }
        return res.text().then(function (text) {
          var data = text ? unwrap(JSON.parse(text)) : null;
          onSubmitResult(data);
        });
      })
      .catch(function () {
        showSubmitError(MSG_SUBMIT_GENERIC);
      })
      .then(function () {
        refs.submitBtn.disabled = false;
      });
  }

  // Handle a 200 submit DTO { result, state, commentId, askerName, message }.
  function onSubmitResult(data) {
    if (data && data.result === "success") {
      var categoryId = parseInt(refs.inCat.value || "0", 10);
      var categoryName = "";
      state.categories.forEach(function (c) {
        if (c.id === categoryId) categoryName = c.name;
      });
      var localQuestion = {
        commentId: data.commentId,
        text: refs.inQuestion.value.trim(),
        askerName: data.askerName || refs.inName.value.trim(),
        createdUtc: new Date().toISOString(),
        categoryId: categoryId,
        categoryName: categoryName,
        voteCount: 0,
        votedByMe: false,
        viewCount: 0,
        answered: false,
        mine: true,
        pending: data.state === "pending"
      };
      state.questions.unshift(localQuestion);
      state.counts.all += 1;
      if (!localQuestion.pending) state.counts.open += 1;

      refs.inQuestion.value = "";
      refs.charCount.textContent = "0 / " + state.characterLimit;
      refs.inCat.value = "0";
      toggleCompose(false);
      refs.okNoticeDesc.textContent =
        data.state === "pending"
          ? "Thank you. The project team reviews new questions before they appear here — yours is visible only to you until it's approved."
          : "Thank you. Your question is now live and the project team has been notified.";
      refs.okNotice.classList.add("pi-qa__notice--show");
      refs.okNotice.focus();
      renderList(false);
    } else if (
      data &&
      (data.result === "closed" ||
        data.result === "invalid" ||
        data.result === "error")
    ) {
      // Server-authored business messages: "closed" (poll closed/archived), "invalid" (over
      // the character limit), "error" (fully-anonymous project). Show the server's message.
      showSubmitError(data.message || MSG_SUBMIT_GENERIC);
    } else {
      showSubmitError(MSG_SUBMIT_GENERIC);
    }
  }

  // Pull the human-readable message out of the Core API failure envelope
  // ({ error: { description, errors:[{ description }] } }); prefer the field-level error.
  function validationMessage(text) {
    if (!text) return "";
    var payload;
    try {
      payload = JSON.parse(text);
    } catch (e) {
      return "";
    }
    var err = payload && payload.error;
    if (!err) return "";
    if (err.errors && err.errors.length && err.errors[0].description)
      return err.errors[0].description;
    return err.description || "";
  }

  function showSubmitError(message) {
    toggleCompose(false);
    refs.errNotice.querySelector(".pi-qa__notice-d").textContent = message;
    refs.errNotice.classList.add("pi-qa__notice--show");
    refs.errNotice.focus();
  }

  function resetAndReload() {
    state.page = 0;
    loadQuestions(false);
  }

  function wireEvents() {
    // Tabs: click + APG arrow-key pattern
    [refs.tabQuestions, refs.tabListening].forEach(function (tab) {
      tab.addEventListener("click", function () {
        switchTab(tab.getAttribute("data-qa-tab"));
      });
      tab.addEventListener("keydown", function (e) {
        var tabs = [refs.tabQuestions, refs.tabListening];
        var index = tabs.indexOf(tab);
        var next = null;
        if (e.key === "ArrowRight" || e.key === "ArrowDown")
          next = tabs[(index + 1) % tabs.length];
        else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
          next = tabs[(index + tabs.length - 1) % tabs.length];
        else if (e.key === "Home") next = tabs[0];
        else if (e.key === "End") next = tabs[tabs.length - 1];
        if (next) {
          e.preventDefault();
          switchTab(next.getAttribute("data-qa-tab"));
          next.focus();
        }
      });
    });

    // Delegated list interactions
    refs.list.addEventListener("click", function (e) {
      var target = e.target.closest("[data-qa-act]");
      if (!target) return;
      var id = parseInt(target.getAttribute("data-qa-id"), 10);
      var q = null;
      state.questions.forEach(function (item) {
        if (item.commentId === id) q = item;
      });
      if (!q) return;

      if (target.getAttribute("data-qa-act") === "vote") {
        vote(q, target);
      } else if (target.getAttribute("data-qa-act") === "answer") {
        var isOpen = !!state.openAnswers[id];
        state.openAnswers[id] = !isOpen;
        target.setAttribute("aria-expanded", (!isOpen).toString());
        var panel = refs.list.querySelector("#" + uid + "-ans-" + id);
        if (panel) panel.classList.toggle("pi-qa__answer--show", !isOpen);
        target.textContent = "";
        target.appendChild(icon("chev"));
        target.appendChild(
          document.createTextNode(
            !isOpen ? " Hide official answer" : " View official answer"
          )
        );
        if (!isOpen) pingView(q);
      }
    });

    if (refs.search) {
      refs.search.addEventListener("input", function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () {
          state.search = refs.search.value.trim();
          resetAndReload();
        }, SEARCH_DEBOUNCE_MS);
      });
    }
    if (refs.status) {
      refs.status.addEventListener("change", function () {
        state.status = refs.status.value;
        resetAndReload();
      });
    }
    if (refs.category) {
      refs.category.addEventListener("change", function () {
        state.categoryId = parseInt(refs.category.value, 10) || 0;
        resetAndReload();
      });
    }
    refs.sort.addEventListener("change", function () {
      state.sort = refs.sort.value;
      resetAndReload();
    });

    refs.showMore.addEventListener("click", function () {
      if (state.loading) return;
      state.page += 1;
      var before = refs.list.children.length;
      loadQuestions(true).then(function () {
        var firstNew = refs.list.children[before];
        if (firstNew) {
          var focusable = firstNew.querySelector("button");
          if (focusable) focusable.focus();
        }
      });
    });

    refs.askBtn.addEventListener("click", function () {
      toggleCompose();
    });
    refs.inQuestion.addEventListener("input", function () {
      refs.charCount.textContent =
        refs.inQuestion.value.length + " / " + state.characterLimit;
    });
    refs.compose.addEventListener("submit", function (e) {
      e.preventDefault();
      submitQuestion();
    });
  }

  buildShell();
  wireEvents();
  root.classList.add("pi-qa--ready");
  refs.resCount.textContent = "Loading questions…";
  loadQuestions(false);
}

/**
 * Initialize every [data-pi-qa] within an optional scope (defaults to document).
 * Safe to call repeatedly — already-initialized instances are skipped.
 * @param {Element|Document} [context]
 */
export function piQaInit(context) {
  var scope = context || document;
  var list = scope.querySelectorAll
    ? scope.querySelectorAll("[data-pi-qa]")
    : [];
  for (var i = 0; i < list.length; i++) {
    var el = list[i];
    if (el.__piQaReady) continue;
    el.__piQaReady = true;
    QaWidget(el);
  }
  if (
    scope !== document &&
    scope.nodeType === 1 &&
    scope.hasAttribute &&
    scope.hasAttribute("data-pi-qa") &&
    !scope.__piQaReady
  ) {
    scope.__piQaReady = true;
    QaWidget(scope);
  }
}

// Expose a global hook for server-rendered / AJAX-inserted markup.
if (typeof window !== "undefined") {
  window.piQaInit = piQaInit;
}

// Self-initialize static markup that is present at load time (project pages).
if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      piQaInit();
    });
  } else {
    piQaInit();
  }
}
